import { DateTime } from "luxon"

import type { SeasonAmpacity, SeasonBoundary, SeasonSegment } from "types/ampacity.types"
import { customValueFormatter } from "helpers/formatters/plots/plots"
import { AMPACITY_AAR_FIELD } from "helpers/config/telemetryFieldDefs"
import { getFieldUnit } from "./telemetryFields"

/**
 * Seasonal ampacity is a configured attribute of the powerline rather than the `ampacity.aar`
 * telemetry, but it is the same quantity, so the unit comes from the same source of truth.
 */
export const SEASONAL_AMPACITY_UNIT = getFieldUnit(AMPACITY_AAR_FIELD) ?? ""

const MONTH_DAY_RE = /^(\d{1,2})-(\d{1,2})$/

/**
 * Parse a seasonal ampacity dictionary keyed by partial dates (MM-DD) into a
 * calendar-ordered list of boundaries. Invalid keys/values are ignored.
 */
export function parseSeasonalAmpacity(
  seasonal: SeasonAmpacity | null | undefined,
): SeasonBoundary[] {
  if (!seasonal) return []

  const boundaries: SeasonBoundary[] = []
  for (const [key, value] of Object.entries(seasonal)) {
    const match = MONTH_DAY_RE.exec(key)
    if (!match) continue

    const month = Number(match[1])
    const day = Number(match[2])
    if (
      typeof value !== "number" ||
      !Number.isFinite(value) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31
    ) {
      continue
    }

    boundaries.push({ month, day, value })
  }

  return boundaries.sort((a, b) => a.month - b.month || a.day - b.day)
}

/**
 * Format a boundary's partial date (month/day) as a localized short label,
 * e.g. "Jun 1". The year is irrelevant and not shown.
 */
export function formatSeasonBoundaryDate(
  boundary: SeasonBoundary,
  locale?: string,
): string {
  return DateTime.fromObject({ year: 2001, month: boundary.month, day: boundary.day })
    .setLocale(locale ?? "en")
    .toFormat("LLL d")
}

/**
 * One line per season with its validity window and value ("Jun 1 – Oct 1: 800 A").
 * Each window runs until the next boundary, wrapping cyclically to the first. A
 * single season is constant all year, so its line has no window.
 */
export function formatSeasonalAmpacityLines(
  seasonal: SeasonAmpacity | null | undefined,
  locale?: string,
): string[] {
  const seasons = parseSeasonalAmpacity(seasonal)
  const formatValue = customValueFormatter({
    suffix: SEASONAL_AMPACITY_UNIT,
    decimalPlaces: 0,
    formatUnits: false,
    locale,
  })

  return seasons.map((season, index) => {
    const next = seasons[(index + 1) % seasons.length]
    const window =
      seasons.length > 1
        ? `${formatSeasonBoundaryDate(season, locale)} – ${formatSeasonBoundaryDate(
            next,
            locale,
          )}: `
        : ""
    return `${window}${formatValue(season.value)}`
  })
}

/**
 * Compact seasonal ampacity for summary-style displays: the value in effect
 * today plus the date window of the active season, e.g. "850 A (Apr 1 – Jul 1)".
 * Returns null when there is no seasonal data; a single season omits the window.
 */
export function formatCurrentSeasonalAmpacity(
  seasonal: SeasonAmpacity | null | undefined,
  locale: string,
): { value: number; label: string } | null {
  const seasons = parseSeasonalAmpacity(seasonal)
  const active = getActiveSeason(seasons, new Date())
  if (!active) return null

  const value = active.current.value
  const formattedValue = customValueFormatter({
    suffix: SEASONAL_AMPACITY_UNIT,
    decimalPlaces: 0,
    formatUnits: false,
    locale,
  })(value)
  const label =
    seasons.length > 1
      ? `${formattedValue} (${formatSeasonBoundaryDate(
          active.current,
          locale,
        )} – ${formatSeasonBoundaryDate(active.next, locale)})`
      : formattedValue

  return { value, label }
}

/**
 * The season in effect at `date` and the boundary that ends it.
 * `current` is the most recent boundary at-or-before the date within the year;
 * if the date falls before the first boundary, it wraps to the last boundary
 * (previous year, cyclic). `next` is the following boundary (wrapping to the
 * first), i.e. where the current season ends. Returns null when empty.
 */
export function getActiveSeason(
  sorted: SeasonBoundary[],
  date: Date,
): { current: SeasonBoundary; next: SeasonBoundary } | null {
  if (sorted.length === 0) return null

  const month = date.getMonth() + 1
  const day = date.getDate()

  const atOrBeforeIndex = sorted.reduce(
    (acc, boundary, index) =>
      boundary.month < month || (boundary.month === month && boundary.day <= day)
        ? index
        : acc,
    -1,
  )
  const index = atOrBeforeIndex >= 0 ? atOrBeforeIndex : sorted.length - 1

  return { current: sorted[index], next: sorted[(index + 1) % sorted.length] }
}

/**
 * Value in effect at `date`, given calendar-ordered boundaries.
 * Returns null when empty. See getActiveSeason for the selection rule.
 */
export function getAmpacityAt(sorted: SeasonBoundary[], date: Date): number | null {
  return getActiveSeason(sorted, date)?.current.value ?? null
}

/**
 * Build the concrete stepped segments covering the visible range [xMin, xMax].
 * Boundaries are materialised for every year that overlaps the range so the
 * step function repeats cyclically. Adjacent segments with equal values are
 * collapsed. Returns [] when there is no seasonal data or the range is empty.
 */
export function buildSeasonalSteps(
  seasonal: SeasonAmpacity | null | undefined,
  xMin: Date,
  xMax: Date,
): SeasonSegment[] {
  const sorted = parseSeasonalAmpacity(seasonal)
  if (sorted.length === 0) return []

  const minMillis = xMin.getTime()
  const maxMillis = xMax.getTime()
  if (!(minMillis < maxMillis)) return []

  if (sorted.length === 1) {
    return [{ startMillis: minMillis, endMillis: maxMillis, value: sorted[0].value }]
  }

  // Every year that overlaps the range, so the step function repeats cyclically.
  const firstYear = xMin.getFullYear() - 1
  const years = Array.from(
    { length: xMax.getFullYear() - xMin.getFullYear() + 3 },
    (_, index) => firstYear + index,
  )

  // Concrete transition timestamps strictly inside (xMin, xMax], ordered in time.
  const transitions = years
    .flatMap((year) =>
      sorted.map((boundary) => ({
        millis: new Date(year, boundary.month - 1, boundary.day).getTime(),
        value: boundary.value,
      })),
    )
    .filter(
      (transition) => transition.millis > minMillis && transition.millis <= maxMillis,
    )
    .sort((a, b) => a.millis - b.millis)

  // Breakpoints: the range start (with the value active there) plus each transition.
  const breakpoints = [
    { millis: minMillis, value: getAmpacityAt(sorted, xMin) as number },
    ...transitions,
  ]

  const rawSegments = breakpoints
    .map((breakpoint, index) => ({
      startMillis: breakpoint.millis,
      endMillis:
        index + 1 < breakpoints.length ? breakpoints[index + 1].millis : maxMillis,
      value: breakpoint.value,
    }))
    .filter((segment) => segment.startMillis < segment.endMillis)

  // Collapse adjacent segments that share the same value.
  const segments: SeasonSegment[] = []
  for (const segment of rawSegments) {
    const last = segments[segments.length - 1]
    if (last && last.value === segment.value) {
      last.endMillis = segment.endMillis
    } else {
      segments.push(segment)
    }
  }
  return segments
}
